import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { CartService, CartSummary, CartItemSummary } from '../../services/cart.service';
import { ConfirmationService } from 'primeng/api';
import { UtilsService } from '../../appSharedModule/services/utils.service';

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss']
})
export class CartComponent implements OnInit {
  cart$: Observable<CartSummary | null>;
  removingItemUuid: string | null = null;
  updatingQuantityItemUuid: string | null = null;
  editingItem: CartItemSummary | null = null;
  editDialogVisible = false;
  confirmingOrder = false;

  constructor(
    private readonly cartService: CartService,
    private readonly confirmationService: ConfirmationService,
    private readonly utils: UtilsService,
    private readonly router: Router
  ) {
    this.cart$ = this.cartService.cart$;
  }

  ngOnInit(): void {
    this.cartService.refreshCart();
  }

  formatTitle(item: CartItemSummary): string {
    const companyName = this.getCompanyName(item);
    const prefix = companyName ? `Extra XCO (ADDP) for ${companyName}` : 'ADDP';
    const pop = this.extractCommercialPop(item);
    const technicalPop = this.extractTechnicalPopId(item);
    const popLabel = pop ? `${pop}${technicalPop ? ` (${technicalPop})` : ''}` : '';
    return popLabel ? `${prefix} @ ${popLabel}` : prefix;
  }

  formatProductList(item: CartItemSummary): string {
    const products = item.payload?.items || [];
    if (!Array.isArray(products) || products.length === 0) {
      return 'No product selected';
    }
    return products
      .map((entry: any) => entry?.name || entry?.product?.name)
      .filter(Boolean)
      .join(', ');
  }

  formatPrice(item: CartItemSummary): string {
    const pricing = item.pricing;
    if (!pricing || typeof pricing.amount !== 'number' || !pricing.currency) {
      return '—';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: pricing.currency
    }).format(pricing.amount);
  }

  formatLineTotal(item: CartItemSummary): string {
    const pricing = item.pricing;
    if (!pricing || typeof pricing.amount !== 'number' || !pricing.currency) {
      return '—';
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: pricing.currency
    }).format(pricing.amount * this.getQuantity(item));
  }

  formatCartTotal(cart: CartSummary): string {
    const total = cart.totals?.['total'];
    const currency = cart.totals?.['currency'] || cart.currency;
    if (typeof total !== 'number' || !currency) {
      return '';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency as string
    }).format(total);
  }

  trackByItem(_index: number, item: CartItemSummary): string {
    return item.uuid;
  }

  getQuantity(item: CartItemSummary): number {
    const quantity = Number(item.payload?.quantity ?? 1);
    if (!Number.isFinite(quantity)) {
      return 1;
    }

    return Math.max(1, Math.trunc(quantity));
  }

  getTotalQuantity(cart: CartSummary | null): number {
    if (!cart?.items?.length) {
      return 0;
    }

    return cart.items.reduce((total, item) => total + this.getQuantity(item), 0);
  }

  updateQuantity(item: CartItemSummary, delta: number): void {
    if (this.updatingQuantityItemUuid || delta === 0) {
      return;
    }

    const nextQuantity = this.getQuantity(item) + delta;
    if (nextQuantity < 1) {
      return;
    }

    const payload = this.clonePayload(item.payload);
    if (!payload) {
      return;
    }

    payload.quantity = nextQuantity;
    this.updatingQuantityItemUuid = item.uuid;
    this.cartService.updateItem(item.uuid, payload).subscribe({
      next: () => {
        this.updatingQuantityItemUuid = null;
      },
      error: () => {
        this.updatingQuantityItemUuid = null;
      }
    });
  }

  isQuantityUpdating(item: CartItemSummary): boolean {
    return this.updatingQuantityItemUuid === item.uuid;
  }

  getTechnicalPopName(item: CartItemSummary): string | null {
    const name = item.payload?.connectionPoint?.crossConnect?.technicalPop?.name;
    if (typeof name === 'string' && name.trim().length > 0) {
      return name.trim();
    }
    return null;
  }

  getOriginalXco(item: CartItemSummary): string | null {
    const xco = item.payload?.xco;
    if (typeof xco === 'string' && xco.trim().length > 0) {
      return xco.trim();
    }
    return null;
  }

  private extractCommercialPop(item: CartItemSummary): string {
    const payload = item.payload || {};
    const pop = payload.pop || {};
    return pop.displayId || pop.name || payload.connectionPoint?.colocationSite || '';
  }

  private extractTechnicalPopId(item: CartItemSummary): string | null {
    const technicalId = item.payload?.connectionPoint?.crossConnect?.technicalPop?.technicalId;
    if (typeof technicalId === 'string' && technicalId.trim().length > 0) {
      return technicalId;
    }
    return null;
  }

  private getCompanyName(item: CartItemSummary): string | null {
    const name = item.payload?.company?.name;
    if (typeof name === 'string' && name.trim().length > 0) {
      return name.trim();
    }
    return null;
  }

  private clonePayload<T>(payload: T): T | null {
    if (!payload) {
      return null;
    }

    try {
      return JSON.parse(JSON.stringify(payload));
    } catch (_error) {
      return payload;
    }
  }

  confirmRemove(item: CartItemSummary): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to remove this line?',
      acceptLabel: 'Remove',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.removeItem(item);
      }
    });
  }

  private removeItem(item: CartItemSummary): void {
    this.removingItemUuid = item.uuid;
    this.cartService.deleteItem(item.uuid).subscribe({
      next: () => {
        this.removingItemUuid = null;
      },
      error: () => {
        this.removingItemUuid = null;
      }
    });
  }

  openEdit(item: CartItemSummary): void {
    this.editingItem = item;
    this.editDialogVisible = true;
  }

  onEditDialogVisibilityChange(visible: boolean): void {
    this.editDialogVisible = visible;
    if (!visible) {
      this.editingItem = null;
    }
  }

  openConfirmOrder(cart: CartSummary): void {
    if (cart.isEmpty || this.confirmingOrder) {
      return;
    }
    this.confirmationService.confirm({
      header: 'Confirm Order',
      message: 'Are you sure you want to place this Extra XCO (ADDP) order?',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Place order',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.confirmOrder(cart);
      }
    });
  }

  private confirmOrder(cart: CartSummary): void {
    if (cart.isEmpty || this.confirmingOrder) {
      return;
    }
    this.confirmingOrder = true;
    this.cartService.confirmOrder().subscribe({
      next: response => {
        this.confirmingOrder = false;
        const requestStatuses = Array.isArray(response.requests)
          ? response.requests
            .map(entry => entry?.status)
            .filter((status): status is string => typeof status === 'string' && status.length > 0)
          : [];
        void this.router.navigate(['/cart/confirmation'], {
          state: {
            cart,
            requestStatuses,
            requestStatus: this.resolveRequestStatus(requestStatuses)
          }
        });
      },
      error: err => {
        this.confirmingOrder = false;
        const serverDetail = err?.error?.detail || err?.error?.message || err?.message || null;
        const description = serverDetail
          ? `Please try again later or contact support if the problem persists.\nError details: ${serverDetail}`
          : 'Please try again later or contact support if the problem persists.';
        this.utils.displayMessage(
          'Unable to confirm order',
          description,
          'error',
          false,
          false,
          { disableCustomClose: true }
        );
      }
    });
  }

  private resolveRequestStatus(statuses: string[]): string {
    if (!Array.isArray(statuses) || statuses.length === 0) {
      return 'pending';
    }
    return statuses[0] || 'pending';
  }
}
